import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

interface Permission {
  id: number;
  codigo: string;
  modulo: string;
  nome: string;
  descricao: string | null;
  categoria: string | null;
}

interface Role {
  id: number;
  grupo_politico_id: number;
  nome: string;
  descricao: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  permissions?: Permission[];
  user_count?: number;
}

const MODULE_NAMES: Record<string, string> = {
  demands: 'Demandas',
  contacts: 'Contatos',
  cities: 'Cidades',
  users: 'Usuários',
  regions: 'Regiões Políticas',
  roles: 'Administração',
  // Removed modules: groups (GOD only), audit (future), settings (code-managed)
};

export default function RolesManagement() {
  const navigate = useNavigate();
  const { session, isAdmin, userData } = useAuth();
  const { can, refreshPermissions } = usePermissionCheck();
  const canAccessReports = isAdmin || can('reports.manage');
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Role | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    permission_ids: [] as number[],
  });
  const [originalFormData, setOriginalFormData] = useState({
    nome: '',
    descricao: '',
    permission_ids: [] as number[],
  });
  const [loadingRoleDetails, setLoadingRoleDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [roleModalTab, setRoleModalTab] = useState<'permissions' | 'users'>('permissions');
  const [roleUsers, setRoleUsers] = useState<any[]>([]);
  const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{ id: number; nome: string } | null>(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  useEffect(() => {
    if (selectedRole && roleModalTab === 'users') {
      if (roleUsers.length === 0 && !loadingRoleUsers) {
        loadRoleUsers(selectedRole.id);
      }
      if (availableUsers.length === 0 && !loadingAvailableUsers && (isAdmin || can('roles.manage'))) {
        loadAvailableUsers(selectedRole.id);
      }
    }
  }, [roleModalTab, selectedRole]);

  const loadData = async () => {
    if (!session?.access_token) return;

    try {
      setLoading(true);
      const [rolesRes, permsRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/roles`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/roles/permissions/by-module`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
      ]);

      const rolesData = await rolesRes.json();
      const permsData = await permsRes.json();

      if (rolesData.success) setRoles(rolesData.data);
      if (permsData.success) setPermissions(permsData.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadRoleDetails = async (roleId: number) => {
    if (!session?.access_token) return;

    // Find the role in the existing list to preserve user_count
    const existingRole = roles.find(r => r.id === roleId);

    setSelectedRole(existingRole || { id: roleId } as Role);
    setEditingRole(null);
    setFormData({ nome: '', descricao: '', permission_ids: [] });
    setOriginalFormData({ nome: '', descricao: '', permission_ids: [] });
    setLoadingRoleDetails(true);
    setRoleModalTab('permissions');
    setRoleUsers([]);
    setExpandedModules(new Set());
    setShowModal(true);

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.success) {
        const roleData = {
          ...data.data,
          user_count: existingRole?.user_count || 0,
        };
        setSelectedRole(roleData);
        setEditingRole(roleData);
        const fd = {
          nome: data.data.nome,
          descricao: data.data.descricao || '',
          permission_ids: data.data.permissions?.map((p: Permission) => p.id) || [],
        };
        setFormData(fd);
        setOriginalFormData(fd);
      }
    } catch (err) {
      setError('Erro ao carregar detalhes do papel');
      setShowModal(false);
      setSelectedRole(null);
    } finally {
      setLoadingRoleDetails(false);
    }
  };

  const loadRoleUsers = async (roleId: number) => {
    if (!session?.access_token) return;

    setLoadingRoleUsers(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/roles/${roleId}/users`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.success) {
        setRoleUsers(data.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar usuários do papel:', err);
      setRoleUsers([]);
    } finally {
      setLoadingRoleUsers(false);
    }
  };

  const loadAvailableUsers = async (roleId: number) => {
    if (!session?.access_token) return;

    try {
      setLoadingAvailableUsers(true);
      const res = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/without-roles`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!res.ok) throw new Error('Erro ao carregar usuários');

      const data = await res.json();
      if (data.success) {
        setAvailableUsers(data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar usuários disponíveis:', err);
      setAvailableUsers([]);
    } finally {
      setLoadingAvailableUsers(false);
    }
  };

  const handleAddUserToRole = async (userId: number, userName: string) => {
    if (!session?.access_token || !selectedRole) return;

    try {
      setIsAddingUser(true);
      const res = await apiFetch(`${API_BASE_URL}/api/v1/roles/${selectedRole.id}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: userId }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erro ao adicionar usuário ao papel');
      }

      // Reload role users
      const updatedUsers = await loadRoleUsers(selectedRole.id);
      
      // Update the user count in selectedRole
      setSelectedRole(prev => prev ? {
        ...prev,
        user_count: (prev.user_count || 0) + 1
      } : null);

      // Also update in the roles list
      setRoles(prev => prev.map(role => 
        role.id === selectedRole.id 
          ? { ...role, user_count: (role.user_count || 0) + 1 }
          : role
      ));

      // Remove added user from available users
      setAvailableUsers(prev => prev.filter(u => u.id !== userId));
      
      // Clear search
      setUserSearchTerm('');
      setShowUserDropdown(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar usuário ao papel');
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleRemoveUserFromRole = async (userId: number) => {
    if (!session?.access_token || !selectedRole || !userToRemove) return;

    try {
      setIsRemovingUser(true);
      const res = await apiFetch(`${API_BASE_URL}/api/v1/roles/${selectedRole.id}/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erro ao remover usuário');
      }

      // Update the users list
      const removedUser = roleUsers.find(u => u.id === userId);
      setRoleUsers(prev => prev.filter(u => u.id !== userId));
      
      // Add the removed user back to available users
      if (removedUser && (isAdmin || can('roles.manage'))) {
        setAvailableUsers(prev => [...prev, removedUser]);
      }
      
      // Update the user count in selectedRole
      setSelectedRole(prev => prev ? {
        ...prev,
        user_count: (prev.user_count || 0) - 1
      } : null);

      // Also update in the roles list
      setRoles(prev => prev.map(role => 
        role.id === selectedRole.id 
          ? { ...role, user_count: (role.user_count || 0) - 1 }
          : role
      ));

      setUserToRemove(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover usuário do papel');
    } finally {
      setIsRemovingUser(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRole(null);
    setEditingRole(null);
    setRoleUsers([]);
    setRoleModalTab('permissions');
  };

  const handleCreate = () => {
    setEditingRole(null);
    setSelectedRole(null);
    setFormData({ nome: '', descricao: '', permission_ids: [] });
    setOriginalFormData({ nome: '', descricao: '', permission_ids: [] });
    setExpandedModules(new Set());
    setRoleModalTab('permissions');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.access_token) return;

    try {
      setSubmitting(true);
      setError('');
      const payload = {
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim() || undefined,
        permission_ids: formData.permission_ids,
      };

      const url = editingRole
        ? `${API_BASE_URL}/api/v1/roles/${editingRole.id}`
        : `${API_BASE_URL}/api/v1/roles`;

      const res = await fetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erro ao salvar papel');
      }

      closeModal();
      loadData();
      refreshPermissions(); // update current user's permissions in case their role changed
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar papel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (role: Role) => {
    if (!session?.access_token) return;

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/roles/${role.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erro ao excluir papel');
      }

      setShowDeleteConfirm(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir papel');
    }
  };

  const togglePermission = (permId: number) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permId)
        ? prev.permission_ids.filter(id => id !== permId)
        : [...prev.permission_ids, permId],
    }));
  };

  const toggleModule = (module: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  };

  const selectAllInModule = (module: string) => {
    const modulePerms = permissions[module] || [];
    const allSelected = modulePerms.every(p => formData.permission_ids.includes(p.id));

    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePerms.map(p => p.id).includes(id))
        : [...prev.permission_ids, ...modulePerms.map(p => p.id).filter(id => !prev.permission_ids.includes(id))],
    }));
  };

  // Temporarily disabled permission check for testing - allow all logged-in users
  // TODO: Re-enable permission check after roles.manage permission is properly configured
  /*
  if (!isAdmin && !can('roles.manage')) {
    return (
      <SettingsContainer>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Configurações</SidebarTitle>
            <SidebarSubtitle>Gerencie sua conta</SidebarSubtitle>
          </SidebarHeader>
          <SidebarNav>
            <NavSection>
              <NavSectionTitle>Conta</NavSectionTitle>
              <NavItem onClick={() => navigate('/configuracoes')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
                </svg>
                <span>Perfil</span>
              </NavItem>
            </NavSection>

            {isAdmin && (
              <NavSection>
                <NavSectionTitle>Grupo Político</NavSectionTitle>
                <NavItem onClick={() => navigate('/configuracoes')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span>Regiões</span>
                </NavItem>
                <NavItem onClick={() => navigate('/configuracoes')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                  <span>Time</span>
                </NavItem>
              </NavSection>
            )}
            
            {(isAdmin || can('roles.manage')) && (
              <NavSection>
                <NavSectionTitle>Segurança</NavSectionTitle>
                <NavItem $active onClick={() => navigate('/papeis')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Papéis e Permissões</span>
                </NavItem>
              </NavSection>
            )}
          </SidebarNav>
        </Sidebar>
        <MainContent>
          <ContentHeader>
            <ContentTitle>Acesso Negado</ContentTitle>
          </ContentHeader>
          <ErrorMessage>Você não tem permissão para gerenciar papéis.</ErrorMessage>
        </MainContent>
      </SettingsContainer>
    );
  }
  */

  const isDirty = !editingRole || (
    formData.nome !== originalFormData.nome ||
    formData.descricao !== originalFormData.descricao ||
    formData.permission_ids.length !== originalFormData.permission_ids.length ||
    formData.permission_ids.some(id => !originalFormData.permission_ids.includes(id))
  );

  return (
    <SettingsContainer>
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>Configurações</SidebarTitle>
          <SidebarSubtitle>Gerencie sua conta</SidebarSubtitle>
        </SidebarHeader>

        <SidebarNav>
          <NavSection>
            <NavSectionTitle>Conta</NavSectionTitle>
            <NavItem onClick={() => navigate('/configuracoes')}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
              </svg>
              <span>Perfil</span>
            </NavItem>
          </NavSection>

          {isAdmin && (
            <NavSection>
              <NavSectionTitle>Grupo Político</NavSectionTitle>
              <NavItem onClick={() => navigate('/configuracoes')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span>Regiões</span>
              </NavItem>
              <NavItem onClick={() => navigate('/configuracoes')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                </svg>
                <span>Time</span>
              </NavItem>
            </NavSection>
          )}
          
          {(isAdmin || can('roles.manage')) && (
            <NavSection>
              <NavSectionTitle>Segurança</NavSectionTitle>
              <NavItem $active onClick={() => navigate('/papeis')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Papéis e Permissões</span>
              </NavItem>
            </NavSection>
          )}

          {canAccessReports && (
            <NavSection>
              <NavSectionTitle>Ferramentas</NavSectionTitle>
              <NavItem onClick={() => navigate('/configuracoes?section=reports')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Relatórios</span>
              </NavItem>
            </NavSection>
          )}
        </SidebarNav>
      </Sidebar>

      <MainContent>
        <MobilePageHeader>
          <MobileBackButton onClick={() => navigate('/configuracoes')}>
            <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Configurações
          </MobileBackButton>
        </MobilePageHeader>
        <MobileContentArea>
        <Header>
          <HeaderContent>
            <Title>Gerenciar Papéis</Title>
            <Subtitle>Crie e gerencie papéis personalizados para seu grupo político</Subtitle>
          </HeaderContent>
          {(isAdmin || can('roles.manage')) && (
            <CreateButton onClick={handleCreate}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Criar Papel
            </CreateButton>
          )}
        </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <RolesGrid>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <RoleCardSkeleton key={i}>
                <RoleHeader>
                  <ShimmerBox width="60%" height="24px" />
                  <ShimmerBox width="40px" height="24px" />
                </RoleHeader>
              </RoleCardSkeleton>
            ))}
          </RolesGrid>
        ) : (
          <RolesGrid>
            {roles.filter(role => role.nome !== 'Super Administrador').map(role => (
              <RoleCard key={role.id} $isSystem={false} onClick={() => loadRoleDetails(role.id)}>
                <RoleHeader>
                  <RoleNameContainer>
                    <RoleName>
                      {role.nome}
                    </RoleName>
                    {role.user_count !== undefined && (
                      <UserCountSubtitle>
                        {role.user_count} {role.user_count === 1 ? 'usuário' : 'usuários'}
                      </UserCountSubtitle>
                    )}
                  </RoleNameContainer>
                  <RoleActions>
                    {(isAdmin || can('roles.manage')) && (
                      <ActionButton onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(role); }} $danger title="Excluir">
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ActionButton>
                    )}
                </RoleActions>
              </RoleHeader>
            </RoleCard>
          ))}
        </RolesGrid>
        )}

          {showModal && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  {loadingRoleDetails
                    ? <ShimmerBox width="200px" height="24px" />
                    : editingRole ? editingRole.nome : 'Criar Novo Papel'
                  }
                </ModalTitle>
                <CloseButton onClick={closeModal}>×</CloseButton>
              </ModalHeader>

              {editingRole && !loadingRoleDetails && (
                <TabsContainer>
                  <Tab $active={roleModalTab === 'permissions'} onClick={() => setRoleModalTab('permissions')}>
                    Permissões
                  </Tab>
                  <Tab $active={roleModalTab === 'users'} onClick={() => setRoleModalTab('users')}>
                    Usuários ({selectedRole?.user_count || 0})
                  </Tab>
                </TabsContainer>
              )}

              {loadingRoleDetails ? (
                <>
                  <ShimmerBox width="100%" height="40px" style={{ marginBottom: '16px' }} />
                  <PermissionsList>
                    {[1, 2, 3, 4, 5].map(i => (
                      <PermissionItem key={i}>
                        <ShimmerBox width="100%" height="40px" />
                      </PermissionItem>
                    ))}
                  </PermissionsList>
                </>
              ) : roleModalTab === 'users' && editingRole ? (
                <UsersContainer>
                  {(isAdmin || can('roles.manage')) && (
                    <AddUserSection>
                      <AddUserInputWrapper ref={dropdownRef}>
                        <SearchInput
                          type="text"
                          placeholder="Buscar usuário para adicionar..."
                          value={userSearchTerm}
                          onChange={(e) => {
                            setUserSearchTerm(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          onFocus={() => setShowUserDropdown(true)}
                        />
                        {showUserDropdown && userSearchTerm && (
                          <UserDropdown>
                            {loadingAvailableUsers ? (
                              <DropdownItem>
                                <Spinner />
                                Carregando...
                              </DropdownItem>
                            ) : (
                              availableUsers
                                .filter(u =>
                                  u.nome?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                                  u.email_contato?.toLowerCase().includes(userSearchTerm.toLowerCase())
                                )
                                .slice(0, 5)
                                .map(user => (
                                  <DropdownItem
                                    key={user.id}
                                    onClick={() => !isAddingUser && handleAddUserToRole(user.id, user.nome)}
                                    disabled={isAddingUser}
                                  >
                                    {isAddingUser ? (
                                      <>
                                        <Spinner style={{ width: '16px', height: '16px', borderWidth: '2px', borderTopColor: 'var(--accent)', borderColor: 'rgba(var(--accent-rgb), 0.3)' }} />
                                        <div style={{ color: '#6b7280' }}>Adicionando...</div>
                                      </>
                                    ) : (
                                      <>
                                        <UserAvatar style={{ width: '32px', height: '32px', fontSize: '12px' }}>
                                          {user.nome?.charAt(0).toUpperCase() || 'U'}
                                        </UserAvatar>
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: '14px' }}>{user.nome}</div>
                                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email_contato || 'Sem email'}</div>
                                        </div>
                                      </>
                                    )}
                                  </DropdownItem>
                                ))
                            )}
                            {!loadingAvailableUsers &&
                              availableUsers.filter(u =>
                                u.nome?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                                u.email_contato?.toLowerCase().includes(userSearchTerm.toLowerCase())
                              ).length === 0 && (
                                <DropdownItem disabled>
                                  Nenhum usuário encontrado
                                </DropdownItem>
                              )}
                          </UserDropdown>
                        )}
                      </AddUserInputWrapper>
                    </AddUserSection>
                  )}
                  {loadingRoleUsers ? (
                    <>
                      {[1, 2, 3].map(i => (
                        <UserItemSkeleton key={i}>
                          <ShimmerBox width="48px" height="48px" style={{ borderRadius: '50%' }} />
                          <div>
                            <ShimmerBox width="150px" height="16px" />
                            <ShimmerBox width="120px" height="14px" style={{ marginTop: '4px' }} />
                          </div>
                        </UserItemSkeleton>
                      ))}
                    </>
                  ) : roleUsers.length === 0 ? (
                    <EmptyUsersState>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="48" height="48">
                        <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                      </svg>
                      <EmptyUsersText>Nenhum usuário tem este papel</EmptyUsersText>
                    </EmptyUsersState>
                  ) : (
                    <UsersList>
                      {roleUsers.map(user => (
                        <UserItem key={user.id}>
                          <UserAvatar>
                            {user.nome?.charAt(0).toUpperCase() || 'U'}
                          </UserAvatar>
                          <UserInfo>
                            <UserName>{user.nome}</UserName>
                            <UserEmail>{user.email_contato || 'Sem email'}</UserEmail>
                          </UserInfo>
                          {(isAdmin || can('roles.manage')) && !(userData?.id === user.id && selectedRole?.nome === 'Administrador') && (
                            <RemoveUserButton onClick={() => setUserToRemove({ id: user.id, nome: user.nome })} title="Remover usuário deste papel">
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </RemoveUserButton>
                          )}
                        </UserItem>
                      ))}
                    </UsersList>
                  )}
                </UsersContainer>
              ) : (
              <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Nome do Papel *</Label>
                <Input
                  type="text"
                  value={formData.nome}
                  onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                  placeholder="Ex: Gerente Regional"
                />
              </FormGroup>

              <FormGroup>
                <Label>Descrição</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descreva as responsabilidades deste papel"
                  rows={3}
                />
              </FormGroup>

              <FormGroup>
                <Label>Permissões</Label>
                <PermissionsContainer>
                  {Object.entries(permissions)
                    .filter(([module]) => !['audit', 'groups', 'settings'].includes(module))
                    .map(([module, perms]) => {
                    const moduleSelected = perms.filter(p => formData.permission_ids.includes(p.id)).length;
                    const moduleTotal = perms.length;
                    const selectionState = moduleSelected === 0 ? 'none' : moduleSelected === moduleTotal ? 'all' : 'partial';

                    return (
                      <ModuleSection key={module}>
                        <ModuleHeader $selectionState={selectionState} onClick={() => toggleModule(module)}>
                          <ModuleTitle>
                            <ExpandIcon $expanded={expandedModules.has(module)}>▶</ExpandIcon>
                            {MODULE_NAMES[module] || module}
                            <PermissionCountBadge $selectionState={selectionState}>
                              {moduleSelected}/{moduleTotal}
                            </PermissionCountBadge>
                          </ModuleTitle>
                          <SelectAllButton
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              selectAllInModule(module);
                            }}
                          >
                            {moduleSelected === moduleTotal ? 'Desmarcar' : 'Selecionar'} Todos
                          </SelectAllButton>
                        </ModuleHeader>

                        {expandedModules.has(module) && (
                          <PermissionsList>
                            {perms.map(perm => (
                              <PermissionItem key={perm.id}>
                                <Checkbox
                                  type="checkbox"
                                  checked={formData.permission_ids.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                />
                                <PermissionLabel>
                                  <PermissionName>{perm.nome} ({perm.codigo})</PermissionName>
                                  {perm.descricao && <PermissionDescription>{perm.descricao}</PermissionDescription>}
                                </PermissionLabel>
                              </PermissionItem>
                            ))}
                          </PermissionsList>
                        )}
                      </ModuleSection>
                    );
                  })}
                </PermissionsContainer>
              </FormGroup>

              <FormActions>
                <CancelButton type="button" onClick={closeModal} disabled={submitting}>
                  Cancelar
                </CancelButton>
                {isDirty && (
                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Spinner />
                        {editingRole ? 'Salvando...' : 'Criando...'}
                      </>
                    ) : (
                      editingRole ? 'Salvar Alterações' : 'Criar Papel'
                    )}
                  </SubmitButton>
                )}
              </FormActions>
            </Form>
            )}
          </ModalContent>
        </Modal>
        )}

        {showDeleteConfirm && (
          <Modal onClick={() => setShowDeleteConfirm(null)}>
            <ConfirmModal onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Confirmar Exclusão</ModalTitle>
              </ModalHeader>
              <ConfirmMessage>
                Tem certeza que deseja excluir o papel "{showDeleteConfirm.nome}"?
                <br />
                Esta ação não pode ser desfeita.
              </ConfirmMessage>
              <FormActions>
                <CancelButton onClick={() => setShowDeleteConfirm(null)}>
                  Cancelar
                </CancelButton>
                <DeleteButton onClick={() => handleDelete(showDeleteConfirm)}>
                  Excluir
                </DeleteButton>
              </FormActions>
            </ConfirmModal>
          </Modal>
        )}

        {userToRemove && (
          <Modal onClick={() => setUserToRemove(null)}>
            <ConfirmModal onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Remover Usuário do Papel</ModalTitle>
              </ModalHeader>
              <ConfirmMessage>
                Tem certeza que deseja remover <strong>{userToRemove.nome}</strong> deste papel?
                <br />
                O usuário perderá todas as permissões associadas a este papel.
              </ConfirmMessage>
              <FormActions>
                <CancelButton onClick={() => setUserToRemove(null)} disabled={isRemovingUser}>
                  Cancelar
                </CancelButton>
                <DeleteButton onClick={() => handleRemoveUserFromRole(userToRemove.id)} disabled={isRemovingUser}>
                  {isRemovingUser ? (
                    <>
                      <Spinner />
                      Removendo...
                    </>
                  ) : (
                    'Remover'
                  )}
                </DeleteButton>
              </FormActions>
            </ConfirmModal>
          </Modal>
        )}
        </MobileContentArea>
      </MainContent>
    </SettingsContainer>
  );
}

// Styled Components
const SettingsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  padding-bottom: 120px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding-bottom: 0;
  }
`;

const Sidebar = styled.aside`
  width: 280px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 40px 0;
  position: fixed;
  top: 56px;
  height: calc(100vh - 56px - 120px);
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 24px 24px 24px;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    padding: 20px 20px 16px 20px;
  }
`;

const SidebarTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const SidebarSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

const SidebarNav = styled.nav`
  padding: 16px 0;
`;

const NavSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const NavSectionTitle = styled.div`
  padding: 8px 24px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;

  @media (max-width: 768px) {
    padding: 14px 20px 8px 20px;
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    border-top: 1px solid #f1f5f9;
  }
`;

const NavItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: ${props => props.$active ? '#f1f5f9' : 'transparent'};
  border: none;
  border-left: 3px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.$active ? '#1e293b' : '#64748b'};
  }

  &:hover {
    background: #f8fafc;

    svg {
      color: var(--accent);
    }

    span {
      color: #1e293b;
    }
  }

  @media (max-width: 768px) {
    padding: 16px 20px;
    border-left-width: 0;
    border-bottom: 1px solid #f1f5f9;
    background: white;

    svg { color: #64748b; }

    span {
      color: #1e293b;
      font-size: 15px;
    }

    &::after {
      content: '›';
      margin-left: auto;
      font-size: 22px;
      color: #cbd5e1;
      line-height: 1;
    }

    &:active { background: #f1f5f9; }
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 280px;
  padding: 40px;

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 56px);
  }
`;

const ContentHeader = styled.div`
  margin-bottom: 32px;
`;

const MobilePageHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
    flex-shrink: 0;
  }
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  color: var(--accent);
  font-size: 15px;
  font-weight: 600;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &:active { opacity: 0.7; }
`;

const MobileContentArea = styled.div`
  @media (max-width: 768px) {
    flex: 1;
    padding: 20px 16px 100px 16px;
    overflow-y: auto;
    box-sizing: border-box;
  }
`;

const ContentTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 16px;
    gap: 12px;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 22px;
    margin: 0 0 4px 0;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: var(--accent-dark);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const RoleCard = styled.div<{ $isSystem: boolean }>`
  background: white;
  border: 1px solid ${props => props.$isSystem ? '#fbbf24' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
    &:active {
      background: #f8fafc;
    }
  }
`;

const RoleCardSkeleton = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const RoleNameContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RoleName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserCountSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`;

const SystemBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  text-transform: uppercase;
`;

const RoleActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 6px;
  background: none;
  border: none;
  color: ${props => props.$danger ? '#dc2626' : '#6b7280'};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${props => props.$danger ? '#fee2e2' : '#f3f4f6'};
  }
`;

const RoleDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    border-radius: 20px 20px 0 0;
    max-height: 88vh;
  }
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  padding: 24px;

  @media (max-width: 768px) {
    border-radius: 20px 20px 0 0;
    max-width: 100%;
    padding: 24px 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--accent)' : '#6b7280'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  margin-bottom: -1px;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.$active ? 'var(--accent)' : '#1e293b'};
  }
`;

const UsersContainer = styled.div`
  padding: 24px;
  max-height: 500px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(88vh - 140px);
  }
`;

const AddUserSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const AddUserInputWrapper = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
`;

const DropdownItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: background 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.disabled ? 'white' : '#f9fafb'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const UserEmail = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const UserItemSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
`;

const EmptyUsersState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
  
  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }
`;

const EmptyUsersText = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const RemoveUserButton = styled.button`
  padding: 6px;
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;

  ${UserItem}:hover & {
    opacity: 1;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #fee2e2;
  }
`;

const Form = styled.form`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-dark);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-dark);
  }
`;

const PermissionsContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: calc(88vh - 260px);
  }
`;

const ModuleSection = styled.div`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const ModuleHeader = styled.div<{ $selectionState: 'none' | 'partial' | 'all' }>`
  padding: 12px 16px;
  background: ${props => {
    if (props.$selectionState === 'all') return '#dbeafe';
    if (props.$selectionState === 'partial') return '#eff6ff';
    return '#f9fafb';
  }};
  border-left: 3px solid ${props => {
    if (props.$selectionState === 'all') return 'var(--accent-dark)';
    if (props.$selectionState === 'partial') return '#93c5fd';
    return 'transparent';
  }};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      if (props.$selectionState === 'all') return '#bfdbfe';
      if (props.$selectionState === 'partial') return '#dbeafe';
      return '#f3f4f6';
    }};
  }
`;

const ModuleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  flex: 1;
`;

const PermissionCountBadge = styled.span<{ $selectionState: 'none' | 'partial' | 'all' }>`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.$selectionState === 'all') return 'var(--accent-dark)';
    if (props.$selectionState === 'partial') return '#60a5fa';
    return '#9ca3af';
  }};
  color: white;
`;

const ModuleHeaderReadOnly = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const ModuleTitleReadOnly = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #374151;
`;

const CountBadge = styled.span`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  background: #9ca3af;
  color: white;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  transition: transform 0.2s;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0)'};
  color: #6b7280;
`;



const SelectAllButton = styled.button`
  padding: 4px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const PermissionsList = styled.div`
  padding: 8px;
`;

const PermissionItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const PermissionItemReadOnly = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
`;

const Checkbox = styled.input`
  margin-top: 2px;
  cursor: pointer;
`;

const PermissionLabel = styled.div`
  flex: 1;
`;

const PermissionName = styled.div`
  font-size: 14px;
  color: #374151;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PermissionDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 14px;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background: var(--accent-dark);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 14px;
  }
`;

const DeleteButton = styled.button`
  padding: 10px 20px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }

  &:disabled {
    background: #f87171;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 14px;
  }
`;

const ConfirmMessage = styled.p`
  margin: 16px 0;
  color: #374151;
  line-height: 1.6;
`;

const ShimmerBox = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  background: linear-gradient(
    90deg,
    #f3f4f6 0%,
    #e5e7eb 20%,
    #f3f4f6 40%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
